import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, X, LayoutTemplate, Star, SlidersHorizontal,
  ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import VerifiedBadge from '../components/shared/VerifiedBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const imgUrl = (src) => {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  return `${API_URL}${src}`;
};

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-100'}`}
      />
    ))}
  </div>
);

const TemplateCard = ({ template, onClick }) => {
  const [imgOk, setImgOk] = useState(true);
  const thumb = imgUrl(template.thumbnail);
  const isFree = template.licensing_type === 'free' || !template.price_cents;

  return (
    <div
      onClick={() => onClick(template)}
      className="group cursor-pointer bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
    >
      <div className="relative bg-gradient-to-br from-gray-50 to-slate-100" style={{ aspectRatio: '4/3' }}>
        {thumb && imgOk ? (
          <img src={thumb} alt={template.name} className="w-full h-full object-cover" onError={() => setImgOk(false)} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <LayoutTemplate className="w-10 h-10 text-gray-300" />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isFree ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>
            {isFree ? 'Free' : `$${(template.price_cents / 100).toFixed(2)}`}
          </span>
        </div>
      </div>
      <div className="p-3">
        <p className="font-medium text-gray-900 text-sm truncate">{template.name}</p>
        {template.creator_name && (
          <div className="flex items-center gap-1.5 mt-1">
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0 overflow-hidden">
              {template.creator_avatar
                ? <img src={template.creator_avatar} alt="" className="w-full h-full object-cover" />
                : (template.creator_name[0] || '?').toUpperCase()
              }
            </div>
            <span className="text-xs text-gray-500 truncate">{template.creator_name}</span>
            <VerifiedBadge verified={template.creator_verified} />
          </div>
        )}
        <div className="flex items-center justify-between mt-1.5">
          <StarRating rating={template.average_rating || 0} />
          {template.purchases_count > 0 && (
            <span className="text-[11px] text-gray-400">{template.purchases_count} sales</span>
          )}
        </div>
      </div>
    </div>
  );
};

const FilterSection = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{title}</h3>
    {children}
  </div>
);

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'trending', label: 'Trending' },
];

export default function MarketplaceBrowsePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [categoryId, setCategoryId] = useState(searchParams.get('category_id') || '');
  const [pricing, setPricing] = useState(searchParams.get('pricing') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [page, setPage] = useState(1);

  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const LIMIT = 20;

  // Load categories once
  useEffect(() => {
    fetch(`${API_URL}/api/marketplace/categories`)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d) => setCategories((d.items || []).filter((c) => c.marketplace_template_count > 0)))
      .catch(() => {});
  }, []);

  // Fetch templates when filters change
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (categoryId) params.set('category_id', categoryId);
      if (pricing) params.set('pricing', pricing);
      if (sort) params.set('sort', sort);
      params.set('page', page);
      params.set('limit', LIMIT);

      const r = await fetch(`${API_URL}/api/marketplace/templates?${params}`);
      if (r.ok) {
        const d = await r.json();
        setTemplates(d.items || []);
        setTotal(d.total || 0);
        setPages(d.pages || 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, categoryId, pricing, sort, page]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Sync URL params
  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (categoryId) params.category_id = categoryId;
    if (pricing) params.pricing = pricing;
    if (sort !== 'newest') params.sort = sort;
    setSearchParams(params, { replace: true });
  }, [search, categoryId, pricing, sort]);

  const applySearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setCategoryId('');
    setPricing('');
    setSort('newest');
    setPage(1);
  };

  const hasActiveFilters = search || categoryId || pricing || sort !== 'newest';

  const Sidebar = () => (
    <div className="space-y-1">
      <FilterSection title="Sort By">
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setSort(opt.value); setPage(1); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              sort === opt.value
                ? 'bg-violet-50 text-violet-700 font-semibold'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </FilterSection>

      <FilterSection title="Pricing">
        {[
          { value: '', label: 'All Templates' },
          { value: 'free', label: 'Free Only' },
          { value: 'one_time', label: 'Paid Only' },
        ].map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setPricing(opt.value); setPage(1); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              pricing === opt.value
                ? 'bg-violet-50 text-violet-700 font-semibold'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </FilterSection>

      {categories.length > 0 && (
        <FilterSection title="Category">
          <button
            onClick={() => { setCategoryId(''); setPage(1); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              !categoryId ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            All Categories
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setCategoryId(cat.id); setPage(1); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                categoryId === cat.id
                  ? 'bg-violet-50 text-violet-700 font-semibold'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span>{cat.name}</span>
              <span className="text-xs text-gray-400">{cat.marketplace_template_count}</span>
            </button>
          ))}
        </FilterSection>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Top bar */}
      <div className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/marketplace')} className="text-sm text-gray-500 hover:text-violet-600 transition-colors">
            Marketplace
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-medium text-gray-800">Browse</span>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Search bar */}
        <div className="flex gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder="Search templates, styles, categories…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={applySearch}
            className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors"
          >
            Search
          </button>
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="md:hidden px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 flex items-center gap-2"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
        </div>

        {hasActiveFilters && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs text-gray-500">Active filters:</span>
            {search && <span className="text-xs bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full font-medium">"{search}"</span>}
            {categoryId && <span className="text-xs bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full font-medium">{categories.find((c) => c.id === categoryId)?.name || categoryId}</span>}
            {pricing && <span className="text-xs bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full font-medium capitalize">{pricing === 'one_time' ? 'Paid' : pricing}</span>}
            {sort !== 'newest' && <span className="text-xs bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full font-medium">{SORT_OPTIONS.find((o) => o.value === sort)?.label}</span>}
            <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-red-500 underline ml-1 transition-colors">
              Clear all
            </button>
          </div>
        )}

        <div className="flex gap-8">
          {/* Sidebar — desktop */}
          <aside className="hidden md:block w-52 flex-shrink-0">
            <div className="sticky top-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <Sidebar />
            </div>
          </aside>

          {/* Mobile filters */}
          {showMobileFilters && (
            <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setShowMobileFilters(false)}>
              <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Filters</h2>
                  <button onClick={() => setShowMobileFilters(false)}><X className="w-5 h-5 text-gray-500" /></button>
                </div>
                <Sidebar />
              </div>
            </div>
          )}

          {/* Grid */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {loading ? '…' : `${total.toLocaleString()} template${total !== 1 ? 's' : ''}`}
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <LayoutTemplate className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">No templates found</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="mt-4 text-sm text-violet-600 hover:underline">
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {templates.map((t) => (
                    <TemplateCard key={t.id} template={t} onClick={(t) => navigate(`/marketplace/templates/${t.id}`)} />
                  ))}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-10">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-9 h-9 rounded-lg border border-gray-200 bg-white flex items-center justify-center disabled:opacity-40 hover:border-violet-400 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {pages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pages, p + 1))}
                      disabled={page === pages}
                      className="w-9 h-9 rounded-lg border border-gray-200 bg-white flex items-center justify-center disabled:opacity-40 hover:border-violet-400 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
